import { useMemo, useState, type ReactNode } from "react";

/** Column sorting for the portal's county tables, defined ONCE.
 *
 *  WHY THIS IS SHARED RATHER THAN WRITTEN TWICE. Two county tables now want the same behaviour, and
 *  hand-rolling a `sort` in each is precisely how the dashboard and portal ended up with two Query<T>
 *  copies that drifted. It is a small enough thing that copying it feels cheaper than sharing it,
 *  which is the trap: the copies do not diverge on the day they are written.
 *
 *  NULLS ALWAYS SORT LAST, in both directions. A county with no measurement is not the best or the
 *  worst; putting `null` at the top of an ascending sort would read as "this is the lowest number",
 *  and that is the same lie as rendering an unmeasured county as 0%. Unknown goes to the bottom and
 *  stays there whichever way the arrow points.
 */
export type SortDir = "asc" | "desc";
export type SortKey = string;

export function useSort<T>(rows: T[], get: (row: T, key: SortKey) => unknown, initial: SortKey, initialDir: SortDir = "desc") {
  const [key, setKey] = useState<SortKey>(initial);
  const [dir, setDir] = useState<SortDir>(initialDir);

  const sorted = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      const av = get(a, key), bv = get(b, key);
      const an = av == null || av === "", bn = bv == null || bv === "";
      if (an && bn) return 0;
      if (an) return 1;          // unknown last, ALWAYS — never "smallest"
      if (bn) return -1;
      const c = typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, { numeric: true });
      return dir === "asc" ? c : -c;
    });
    return out;
  }, [rows, key, dir, get]);

  /** Click the current column to flip direction; a new column starts descending, because every
   *  number on these pages is one where "most" is the interesting end. */
  const toggle = (k: SortKey) => {
    if (k === key) setDir(dir === "asc" ? "desc" : "asc");
    else { setKey(k); setDir("desc"); }
  };
  return { sorted, key, dir, toggle };
}

export function SortTh({ label, col, sort, align = "left", title, width }: {
  label: ReactNode; col: SortKey;
  sort: { key: SortKey; dir: SortDir; toggle: (k: SortKey) => void };
  align?: "left" | "right"; title?: string; width?: number;
}) {
  const active = sort.key === col;
  return (
    <th
      onClick={() => sort.toggle(col)}
      title={title ?? `Sort by ${typeof label === "string" ? label : col}`}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      style={{
        padding: "7px 10px", borderBottom: "1px solid var(--border,#e5e7eb)", fontWeight: 600,
        whiteSpace: "nowrap", textAlign: align, cursor: "pointer", userSelect: "none",
        minWidth: width,
      }}
    >
      {label}
      <span style={{ opacity: active ? 0.9 : 0.25, marginLeft: 5, fontSize: 10 }}>
        {active ? (sort.dir === "asc" ? "▲" : "▼") : "▼"}
      </span>
    </th>
  );
}
